import type React from 'react';

import { PwaInstallPromptProvider } from '@mfw/components';
import { themeVarName } from '@mfw/common';
import { Helmet } from 'react-helmet-async';

import useTenantSplashScreen from '../../hooks/tenants/useTenantSplashScreen';
import { useOrganizationContext } from '../OrganizationProvider';
import { useAppThemeContext } from '../AppThemeProvider';
import { useTenantContext } from '../TenantProvider';
import { useAuthContext } from '../AuthProvider';
import { apiBaseUrl } from '../../api/axios';
import config from '../../config';

const STATIC_APP_ICON = '/public/icon_192x192.png';

export type ManifestProviderProps = {
  /**
   * Whether this route may ask the visitor to install the app.
   *
   * `false` on the anonymous public page (`publicSiteRoutes.tsx`): it is
   * reached by a link an owner shared with someone who has no account, and
   * on a phone the prompt renders a full-screen scrim over the page (iOS
   * shows it on the FIRST visit, with no event to wait for) — so the one
   * thing the link was sent for is covered by an offer to install an app
   * the visitor has no use for.
   *
   * Required rather than defaulted here: the sibling `<Helmet>` block makes
   * this component look like pure page metadata, and the prompt is the one
   * thing in it that puts UI on the screen.
   */
  canPromptAppInstall: boolean;
  /**
   * Whether this branch may SHOW the prompt (it always listens when
   * `canPromptAppInstall`). `false` on the `/auth` branch: `routes.tsx` keys
   * `/auth` and `/` apart, and signing in or finishing onboarding replaces the
   * `/auth` tree a render later — a prompt shown there vanishes unseen and
   * spends the device's one show (Android flag, iOS visit counter).
   */
  canShowAppInstallPrompt: boolean;
  /**
   * The app, rendered beneath the install provider: the menu's "Install the
   * app" entry reads it (`usePwaInstallPromptContext`).
   */
  children?: React.ReactNode;
};

export const ManifestProvider = ({
  canPromptAppInstall,
  canShowAppInstallPrompt,
  children,
}: ManifestProviderProps) => {
  const { theme } = useAppThemeContext();
  const { status: tenantStatus, tenant } = useTenantContext();
  const {
    currentUser,
    isAuthenticated,
    isLoading: isAuthLoading,
  } = useAuthContext();
  const { brandingSource, isLoading: isOrganizationLoading } =
    useOrganizationContext();

  // Shown only to an onboarded account: on the register form the sheet
  // covered the fields and the submit button, and its scrim ate the first
  // tap. The provider is still mounted from the start, because Chrome's
  // install offer arrives once per page load — often before the account has
  // loaded — and only a mounted provider hears it.
  // Nor before the tenant has loaded: the sheet is shown once per device, and
  // that one show would carry the bundled icon instead of the tenant's.
  const canPresentInstallPrompt =
    canShowAppInstallPrompt &&
    tenantStatus !== 'pending' &&
    isAuthenticated &&
    Boolean(currentUser?.hasFinishedOnboarding);

  // Rebrand: prefer the resolved branding logo as favicon under the same
  // rule as the in-app Logo. The theme-color meta below reads the merged
  // `theme` straight from AppThemeProvider, so it already reflects any
  // org/corp overrides.
  const orgLogoUrl = brandingSource?.logoUrl;

  const { devicePixelRatio, screenHeight, screenWidth, splashUrl } =
    useTenantSplashScreen({ splashScreens: tenant?.splashScreens });

  // The manifest is generated per tenant by the API, and reached through the
  // same base as every other call — which nginx proxies under /api in every
  // deployed variant, so it is same-origin there.
  //
  // NOT in local dev, where the API sits on its own port. A manifest's
  // `start_url` and `scope` resolve against the MANIFEST's url, not the
  // document's, so a cross-origin one is rejected as out-of-scope no matter
  // what `crossOrigin` says — and the app would look uninstallable locally
  // while being fine everywhere else, which is worse than having no manifest to
  // test with. The bundled static one is unbranded but valid, so what dev shows
  // is true.
  const manifestHref = config.environment.isLocal
    ? '/public/manifest.json'
    : `${apiBaseUrl}/v2/tenant/manifest?domain=${encodeURIComponent(
        config.cloud.defaults.tenantDomain,
      )}`;

  // Generated derivatives at real icon sizes. Without them the browser would
  // download the full-size logo (~1000px) to paint a 16px favicon.
  //
  // `||`, never `??`: every step of this chain can legitimately be an EMPTY
  // STRING rather than null — `mapTenantFromApi` normalizes a missing logo to
  // `''`, and `brandingSource.logoUrl` carries whatever the org record holds.
  // `??` walks straight past those, and `<link rel="icon" href="">` resolves to
  // the document URL, so the browser fetches the SPA's own HTML as the favicon.
  const faviconHref =
    orgLogoUrl ||
    tenant?.iconUrls?.icon32 ||
    // `logoUrl`, not `logoName` — the latter is the bare stored filename, and a
    // relative `<link rel="icon" href="abc.png">` resolves against the SPA
    // origin, where nginx answers with index.html.
    tenant?.logoUrl ||
    STATIC_APP_ICON;
  const appleTouchIconHref = tenant?.iconUrls?.icon180 || STATIC_APP_ICON;
  // The install sheet shows the icon the home screen will get: the manifest's
  // 192px one, the tenant's and never an organization's.
  const installPromptIconHref = tenant?.iconUrls?.icon192 || STATIC_APP_ICON;
  // Safari keeps the FIRST icon a page declares and ignores every later swap.
  // Declaring the bundled placeholder while the tenant, the user or the
  // organization list was still loading pinned the MFW icon there for good, so
  // no icon is declared until the branding it is chosen from has settled.
  const isBrandingSettled =
    tenantStatus !== 'pending' &&
    !isAuthLoading &&
    !(isAuthenticated && isOrganizationLoading);

  return (
    <>
      <Helmet>
        <title>{tenant?.name}</title>
        <meta content={tenant?.description} name="description" />
        <meta
          content="Mfw, MyFitWorld, My Fit World, fitness, training, workout, program, exercise, trainer"
          name="keywords"
        />
        <meta content={theme[themeVarName.primary.main]} name="theme-color" />

        {/* No `crossOrigin`: this is same-origin in every variant now — /api
            behind nginx when deployed, the bundled file in local dev. */}
        <link href={manifestHref} rel="manifest" />

        {/* Icons */}

        {/** TODO Add splash screen per device size. Can be just loader in colors of app */}
        {/* <link rel="apple-touch-startup-image" href="/splashscreens/iphone12.png" media="(device-width: 390px) and (device-height: 844px) and (-webkit-device-pixel-ratio: 3)"> */}
        {splashUrl && (
          <link
            media={`(device-width: ${screenWidth}px) and (device-height: ${screenHeight}px) and (-webkit-device-pixel-ratio: ${devicePixelRatio})`}
            rel="apple-touch-startup-image"
            href={splashUrl}
          />
        )}
        {isBrandingSettled && (
          <link href={appleTouchIconHref} rel="apple-touch-icon" />
        )}

        {isBrandingSettled && <link href={faviconHref} rel="icon" />}

        {/* iOS splash (optional — needs many sizes if you want full support) */}
        <meta name="apple-mobile-web-app-status-bar-style" content="default" />
        <meta name="apple-mobile-web-app-title" content={tenant?.name} />
      </Helmet>
      {canPromptAppInstall ? (
        <PwaInstallPromptProvider
          appIconPath={installPromptIconHref}
          canPresent={canPresentInstallPrompt}
        >
          {children}
        </PwaInstallPromptProvider>
      ) : (
        children
      )}
    </>
  );
};
